import type {
  PostingCategoryKind,
  PostingExprNode,
  PostingExprOrNode,
  PostingExprRangeNode,
  PostingExprTextNode,
  PostingExprValueNode,
  PostingNode,
  PostingRangeKind,
  PostingTextKind,
} from '@revelio/data-access';

import type { TreeFilter } from '../../../filters/types/filters';
import { getActiveSelections } from '../../../filters/utils/primaryFilterUtils';

const buildPostingValueNode = (exprNode: PostingExprNode): PostingNode => ({
  posting_value: { child: exprNode },
});

const buildPostingAndNode = (children: PostingNode[]): PostingNode => ({
  posting_and: { children },
});

const buildPostingNotNode = (child: PostingNode): PostingNode => ({
  posting_not: { child },
});

export const wrapExprNode = (
  exprNode: PostingExprNode,
  isNot?: boolean
): PostingNode => {
  const valueNode = buildPostingValueNode(exprNode);
  return isNot ? buildPostingNotNode(valueNode) : valueNode;
};

export const buildValNode = (
  kind: PostingCategoryKind,
  id: number
): { val: PostingExprValueNode } => ({
  val: { attribute: { kind, id } },
});

export type RefData = {
  level: number;
  taxonomyId: number;
};

export const buildRoleValNode = (
  id: number,
  ref: RefData
): { val: PostingExprValueNode } => ({
  val: { role: { id, level: ref.level, taxonomy_id: ref.taxonomyId } },
});

export const buildSkillValNode = (
  id: number,
  ref: RefData
): { val: PostingExprValueNode } => ({
  val: { skill: { id, level: ref.level, taxonomy_id: ref.taxonomyId } },
});

export const buildOrNode = (
  children: PostingExprNode[]
): { or: PostingExprOrNode } => ({
  or: { children },
});

const flattenPostingAndNodes = (node: PostingNode): PostingNode[] => {
  if (node.posting_and?.children) {
    return node.posting_and.children.flatMap(flattenPostingAndNodes);
  }
  return [node];
};

export const composeFilterNodes = (
  nodes: Array<PostingNode | PostingNode[] | null>
): PostingNode => {
  const flattenedNodes = nodes
    .flatMap((node) => {
      if (node === null) return [];
      if (Array.isArray(node)) return node;
      return [node];
    })
    .flatMap(flattenPostingAndNodes);

  if (flattenedNodes.length === 0) {
    return buildPostingAndNode([]);
  }
  if (flattenedNodes.length === 1) {
    const [singleNode] = flattenedNodes;
    return singleNode ?? buildPostingAndNode([]);
  }
  return buildPostingAndNode(flattenedNodes);
};

export const idsToNode = (
  ids: number[],
  kind: PostingCategoryKind
): PostingExprNode | null => {
  if (ids.length === 0) return null;
  const [firstId] = ids;
  if (ids.length === 1 && firstId !== undefined) {
    return buildValNode(kind, firstId);
  }
  return buildOrNode(ids.map((id) => buildValNode(kind, id)));
};

const extractNumericIdFromSelection = (selectionId: string): number | null => {
  const parts = selectionId.split('_');
  const lastPart = parts[parts.length - 1];
  if (!lastPart) return null;
  const numericId = parseInt(lastPart, 10);
  return Number.isFinite(numericId) ? numericId : null;
};

export const buildTreeFilterNodes = (
  filter: TreeFilter,
  makeLeaf: (numericId: number, levelId: string) => PostingExprNode | null
): PostingExprNode | null => {
  const selections = filter.primarySelections
    ? getActiveSelections(filter.primarySelections)
    : filter.selections;
  if (!selections || selections.length === 0) return null;

  // Group leaves by levelId
  const groupedByLevelId: Record<string, PostingExprNode[]> = {};
  for (const selection of selections) {
    const numericId = extractNumericIdFromSelection(selection.id);
    if (numericId === null) continue;
    const leaf = makeLeaf(numericId, selection.levelId);
    if (!leaf) continue;
    (groupedByLevelId[selection.levelId] ??= []).push(leaf);
  }

  const levelNodes = Object.values(groupedByLevelId)
    .map((leaves): PostingExprNode | null => {
      if (leaves.length === 0) return null;
      if (leaves.length === 1) return leaves[0] ?? null;
      return buildOrNode(leaves);
    })
    .filter((node): node is PostingExprNode => node !== null);

  if (levelNodes.length === 0) return null;
  if (levelNodes.length === 1) {
    const [singleNode] = levelNodes;
    return singleNode ?? null;
  }
  return buildOrNode(levelNodes);
};

export const buildRangeNode = (
  kind: PostingRangeKind,
  start: number,
  end: number
): { rng: PostingExprRangeNode } => ({
  rng: { attribute: { kind, start, end } },
});

export const buildTxtNode = (
  kind: PostingTextKind,
  text: string
): { txt: PostingExprTextNode } => ({
  txt: { attribute: { kind, text } },
});
